/**
 * POWERHOUSE-INGEST-01 — subcontractor invoice ledger (client side).
 *
 * parse-powerhouse (edge fn) reads Powerhouse Electric's emails from the
 * service@ mailbox and files one ledger row per invoice PDF: amount from the
 * PDF, permit number + ESC job number from the email body, customer resolved
 * through the READ-ONLY ESC mirror. Rows arrive as status='review' and NOTHING
 * touches a job, permit, or customer file until a human clicks Apply here.
 *
 * Apply writes the REAL records (Seth 2026-08-19: "the permit number …
 * added into the system - not just saying 'pulled by powerhouse'"):
 *   1. a pegasus_permit row carrying the permit number (job-linked when a
 *      Pegasus job exists; customer-linked for ESC-era installs),
 *   2. a subcontractor cost line in job costing (when a Pegasus job exists),
 *   3. the invoice PDF filed on the customer's documents (and the job's).
 * Forward-to-GM sends the ORIGINAL email (attachments intact) on to the GM
 * for payment — replaces the print-and-hand-over ritual.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface SubInvoice {
  id: string;
  message_id: string;
  attachment_name: string | null;
  sub_name: string;
  invoice_no: string | null;
  invoice_date: string | null;
  amount: number | null;
  esc_dispatch: string | null;
  permit_no: string | null;
  custno: string | null;
  locno: string | null;
  customer_name: string | null;
  address: string | null;
  city: string | null;
  matched_job_id: string | null;
  match_type: string;
  status: string; // review | applied | dismissed
  applied_at: string | null;
  applied_by: string | null;
  permit_id: string | null;
  cost_material_id: string | null;
  gm_routed_at: string | null;
  gm_routed_to: string | null;
  pdf_storage_path: string | null;
  created_at: string;
}

const invalidate = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ["sub_invoices"] });
  qc.invalidateQueries({ queryKey: ["job_permits"] });
  qc.invalidateQueries({ queryKey: ["job_materials"] });
  qc.invalidateQueries({ queryKey: ["customer_files"] });
  qc.invalidateQueries({ queryKey: ["files"] });
};

/** All invoices in a status (the Jobs-page review rail wants 'review'). */
export function useSubInvoices(status: "review" | "applied" | "all" = "review") {
  return useQuery({
    queryKey: ["sub_invoices", status],
    queryFn: async (): Promise<SubInvoice[]> => {
      let q = supabase.from("pegasus_sub_invoice").select("*").order("invoice_date", { ascending: false }).order("invoice_no", { ascending: false });
      if (status !== "all") q = q.eq("status", status);
      const { data } = await q;
      return (data ?? []) as SubInvoice[];
    },
  });
}

/** Invoices attached to one Pegasus job (Money & Close-out tab). */
export function useJobSubInvoices(jobId: string | null) {
  return useQuery({
    queryKey: ["sub_invoices", "job", jobId],
    enabled: !!jobId,
    queryFn: async (): Promise<SubInvoice[]> => {
      const { data } = await supabase.from("pegasus_sub_invoice").select("*").eq("matched_job_id", jobId!).neq("status", "dismissed").order("invoice_date");
      return (data ?? []) as SubInvoice[];
    },
  });
}

/** Short-lived signed URL for the stored invoice PDF. */
export async function subInvoicePdfUrl(path: string): Promise<string | null> {
  const { data } = await supabase.storage.from("customer-files").createSignedUrl(path, 600);
  return data?.signedUrl ?? null;
}

/** APPLY — the one human click that writes the real records. */
export function useApplySubInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inv: SubInvoice) => {
      const who = (await supabase.auth.getUser()).data.user?.email ?? "office";
      const jobId = inv.matched_job_id;

      // 1. REAL permit entry (job-linked when possible, else customer-linked).
      let permitId: string | null = null;
      if (inv.permit_no) {
        const { data: p, error: pErr } = await supabase
          .from("pegasus_permit")
          .insert({
            job_id: jobId,
            custno: inv.custno,
            locno: inv.locno,
            permit_no: inv.permit_no,
            jurisdiction: inv.city,
            trade: "Electrical",
            status: "issued",
            pulled_by: "Powerhouse Electric",
            pulled_at: inv.invoice_date,
            source: "powerhouse",
            sub_invoice_id: inv.id,
            notes: `From Powerhouse invoice ${inv.invoice_no ?? "?"}${inv.esc_dispatch ? ` · ESC job ${inv.esc_dispatch}` : ""}`,
          })
          .select("id")
          .single();
        if (pErr) throw pErr;
        permitId = p?.id ?? null;
      }

      // 2. Subcontractor cost line in job costing (Pegasus jobs only).
      let matId: string | null = null;
      if (jobId && inv.amount != null) {
        const { data: mrow, error: mErr } = await supabase
          .from("pegasus_job_material")
          .insert({
            job_id: jobId,
            description: `Powerhouse Electric — electrical (invoice ${inv.invoice_no ?? "?"})`,
            kind: "subcontractor",
            qty: 1,
            unit: "ea",
            unit_cost: inv.amount,
            print: false,
          })
          .select("id")
          .single();
        if (mErr) throw mErr;
        matId = mrow?.id ?? null;
      }

      // 3. File the PDF on the customer (and job, when linked).
      if (inv.pdf_storage_path && inv.custno) {
        await supabase.from("pegasus_file").insert({
          custno: inv.custno,
          locno: inv.locno,
          job_id: jobId,
          kind: "document",
          filename: inv.attachment_name ?? `Powerhouse ${inv.invoice_no ?? "invoice"}.pdf`,
          storage_path: inv.pdf_storage_path,
          mime: "application/pdf",
          caption: `Powerhouse Electric invoice${inv.invoice_no ? ` #${inv.invoice_no}` : ""}${inv.amount != null ? ` · $${inv.amount}` : ""}`,
          uploaded_by: "parse-powerhouse",
          source: "powerhouse",
        });
      }

      const { error } = await supabase
        .from("pegasus_sub_invoice")
        .update({ status: "applied", applied_at: new Date().toISOString(), applied_by: who, permit_id: permitId, cost_material_id: matId, updated_at: new Date().toISOString() })
        .eq("id", inv.id);
      if (error) throw error;
    },
    onSuccess: () => invalidate(qc),
  });
}

export function useDismissSubInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pegasus_sub_invoice").update({ status: "dismissed", updated_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => invalidate(qc),
  });
}

/** Correct a match before applying (office picks the right job / clears one). */
export function useRelinkSubInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; matched_job_id: string | null }) => {
      const { error } = await supabase.from("pegasus_sub_invoice").update({ matched_job_id: v.matched_job_id, updated_at: new Date().toISOString() }).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => invalidate(qc),
  });
}

/** Forward the ORIGINAL Powerhouse email (PDFs attached) to the GM. */
export function useForwardSubInvoiceToGm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.functions.invoke("parse-powerhouse", { body: { mode: "forward_gm", sub_invoice_id: id } });
      if (error) throw error;
      if (data && data.ok === false) throw new Error(data.error === "no_gm_email" ? "no_gm_email" : (data.detail ?? data.error ?? "forward failed"));
      return data as { ok: true; routed_to: string };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sub_invoices"] }),
  });
}

// ── GM routing settings (org_setting; writes need a Pegasus admin) ──────────
// org_setting.value is JSONB with NATIVE types (house style: comms_live is a
// real boolean) — so the email is a json string and the auto flag a boolean.
export function useGmSettings() {
  return useQuery({
    queryKey: ["org_setting", "gm_invoice"],
    queryFn: async () => {
      const { data } = await supabase.from("org_setting").select("key, value").in("key", ["gm_invoice_email", "gm_invoice_auto"]);
      const map = new Map<string, unknown>((data ?? []).map((r) => [r.key as string, r.value as unknown]));
      const rawEmail = map.get("gm_invoice_email");
      const rawAuto = map.get("gm_invoice_auto");
      return {
        email: typeof rawEmail === "string" ? rawEmail : "",
        auto: rawAuto === true || rawAuto === "true",
      };
    },
  });
}

export function useSaveGmSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { email: string; auto: boolean }) => {
      const who = (await supabase.auth.getUser()).data.user?.email ?? "office";
      const rows: { key: string; value: string | boolean; updated_at: string; updated_by: string }[] = [
        { key: "gm_invoice_email", value: v.email.trim(), updated_at: new Date().toISOString(), updated_by: who },
        { key: "gm_invoice_auto", value: v.auto, updated_at: new Date().toISOString(), updated_by: who },
      ];
      const { error } = await supabase.from("org_setting").upsert(rows, { onConflict: "key" });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["org_setting", "gm_invoice"] }),
  });
}
